import React, { useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { CampaignCardProps } from "../ActionItem";
import { ReconnectAccountsMetaDto } from "../../../common/dto/actions/reconnect-accounts-meta.dto";
import { ActionCloseButton } from "../ActionCloseButton";
import { ActionCardCompleted } from "../ActionCardCompleted";
import { IntegrationsIcon } from "../../../icons";
import { ActionStatus } from "../../../common/types/actions";
import { Button } from "../../general/Button/Button";
import { ActionCardSection } from "../ActionCardSection";
import { ActionCard } from "../ActionCard";
import { pluralize } from "../../../common/helpers/string";
import { financialConnectionsQueryKey } from "../../../queryClient";
import { getUserIntegrations } from "../../../lib/user";
import { GridTable } from "../../general/Tables/GridTable/GridTable";
import { FixFinancialConnection } from "../../financialAccount/FixFinancialConnection";
import { AccountRow } from "./AccountRow";
import styles from "./ReconnectAccounts.module.scss";

export const ReconnectAccountsCard: React.FC<
    CampaignCardProps<ReconnectAccountsMetaDto>
> = ({ action, onConfirm, onSkip, isLoading, ...rest }) => {
    const { isCompleted } = rest;

    const skipAction = useCallback(() => {
        onSkip?.(action);
    }, [action, onSkip]);

    const handleFixed = useCallback(() => {
        onConfirm?.(action);
    }, [action, onConfirm]);

    const integrations = useQuery({
        queryKey: [financialConnectionsQueryKey],
        queryFn: () => getUserIntegrations(),
    });

    const connectionToFix = useMemo(() => {
        if (!integrations.data) {
            return null;
        }

        return (
            integrations.data.financialConnections.find(
                (ufc) =>
                    ufc.connection.id === action.meta.financialConnectionId,
            )?.connection ?? null
        );
    }, [action.meta.financialConnectionId, integrations.data]);

    const disconnectedAccounts = useMemo(() => {
        if (!connectionToFix) {
            return [];
        }

        return connectionToFix.accounts
            .map((acc) => acc.financialAccount)
            .filter((acc) => acc !== null);
    }, [connectionToFix]);

    const firstSection = (inOpened: boolean, closeCard: () => void) => {
        if (isCompleted) {
            return (
                <ActionCardCompleted header="Accounts reconnected">
                    <ActionCloseButton onClick={closeCard} />
                </ActionCardCompleted>
            );
        }

        return (
            <ActionCardSection
                inOpened={inOpened}
                header={
                    <>
                        Your {action.meta.disconnectedAccountsLabel}{" "}
                        {pluralize(
                            "account",
                            action.meta.disconnectedAccountsCount,
                        )}{" "}
                        {action.meta.disconnectedAccountsCount === 1
                            ? "has"
                            : "have"}{" "}
                        been disconnected
                    </>
                }
                icon={<IntegrationsIcon />}
                isTitle
                ctas={
                    <>
                        {action.status !== ActionStatus.SKIPPED && (
                            <Button variant="secondary" onClick={skipAction}>
                                Skip
                            </Button>
                        )}

                        {connectionToFix && (
                            <FixFinancialConnection
                                connection={connectionToFix}
                                onFixed={handleFixed}
                                variant="action"
                            >
                                Reconnect
                            </FixFinancialConnection>
                        )}
                    </>
                }
            ></ActionCardSection>
        );
    };

    const otherSections = (inOpened: boolean) => (
        <ActionCardSection inOpened={inOpened}>
            <p className="mb-5">
                This may happen because some institutions require you to
                re-authenticate from time to time.
                <br /> Please reconnect the{" "}
                {pluralize(
                    "account",
                    action.meta.disconnectedAccountsCount,
                )}{" "}
                below.
            </p>

            {connectionToFix && (
                <section className={styles.section}>
                    {disconnectedAccounts.length > 0 && (
                        <GridTable className={styles.table}>
                            {disconnectedAccounts.map((account) => (
                                <AccountRow
                                    connection={connectionToFix}
                                    account={account}
                                    key={account.id}
                                />
                            ))}
                        </GridTable>
                    )}
                </section>
            )}
        </ActionCardSection>
    );

    return (
        <ActionCard
            {...rest}
            firstSection={firstSection}
            otherSections={otherSections}
        />
    );
};
